import axios from 'axios'

import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import { getResource, getResources } from '@/features/resource/endpoints/resources'
import type { Resource } from '@/features/resource/types'

/** A bare UUID (the shape a user pastes), so we can resolve it by id instead of name-searching. */
const RESOURCE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** How many name matches to pull back before narrowing to local-control resources. */
const SEARCH_RESULT_LIMIT = 25

function isLocalControl(resource: Pick<Resource, 'controlMode'>): boolean {
  return resource.controlMode === 'LOCAL'
}

function toResourceSetMember(resource: Resource): ResourceSetMember {
  return { resourceId: resource.resourceID, name: resource.resourceName ?? resource.resourceID }
}

async function resolveResourceById(resourceId: string): Promise<ResourceSetMember[]> {
  try {
    const { resourceResponse } = await getResource(resourceId)
    return isLocalControl(resourceResponse) ? [toResourceSetMember(resourceResponse)] : []
  } catch (error) {
    // A pasted id that doesn't resolve is simply "no match", not a hard failure.
    if (axios.isAxiosError(error) && error.response?.status === 404) return []
    throw error
  }
}

/**
 * Live resource search for the add-resources panel, scoped to local-control
 * resources (`controlMode === 'LOCAL'`). A pasted resource UUID is resolved
 * directly by id; any other text is a free-text name search. Both paths drop
 * resources that aren't local-control.
 *
 * Backed by the (deployed) Resources Manager API — this never needed the
 * PQ-specific `/local/resources` endpoint, which is why it can be live while the
 * rest of local PQ still runs against mocks.
 */
export async function searchLocalControlResources(query: string): Promise<ResourceSetMember[]> {
  const trimmed = query.trim()
  if (!trimmed) return []

  if (RESOURCE_UUID_PATTERN.test(trimmed)) {
    return resolveResourceById(trimmed)
  }

  const { data } = await getResources({
    filters: { searchKey: trimmed },
    pagination: { limit: SEARCH_RESULT_LIMIT },
  })
  return data.filter(isLocalControl).map(toResourceSetMember)
}
